// Iter58 — Settings → Currency (Centralized Currency Management)
// Platform-first design: base currency + rate table + audit history.
// USD→INR is editable; EUR/GBP/AUD/CAD are scaffolded but disabled
// until the FX API integration ships. Rate changes propagate instantly
// across the marketplace via virtual (read-time) INR calculation.
import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, RefreshCw, TrendingUp, Lock, Info } from "lucide-react";

// Currencies we plan to support — INR is live today; the rest are
// disabled placeholders that pre-shape the UI so we don't have to
// redesign this screen when Phase 2 adds live FX feeds.
const CURRENCY_ROWS = [
  { code: "INR", label: "Indian Rupee",   flag: "🇮🇳", symbol: "₹", enabled: true  },
  { code: "EUR", label: "Euro",           flag: "🇪🇺", symbol: "€", enabled: false },
  { code: "GBP", label: "British Pound",  flag: "🇬🇧", symbol: "£", enabled: false },
  { code: "AUD", label: "Australian Dollar", flag: "🇦🇺", symbol: "$", enabled: false },
  { code: "CAD", label: "Canadian Dollar",   flag: "🇨🇦", symbol: "$", enabled: false },
];

const AdminCurrency = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/currency");
      setConfig(data);
      const initial = {};
      CURRENCY_ROWS.forEach((r) => {
        initial[r.code] = (data.rates || {})[r.code] ?? "";
      });
      setDrafts(initial);
    } catch (e) {
      toast.error("Failed to load", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dirty = config
    ? CURRENCY_ROWS.some((r) => {
        const current = (config.rates || {})[r.code];
        const draft = drafts[r.code];
        const norm = draft === "" ? null : Number(draft);
        return (current ?? null) !== (norm ?? null);
      })
    : false;

  const save = async () => {
    setSaving(true);
    try {
      const rates = {};
      CURRENCY_ROWS.forEach((r) => {
        if (!r.enabled) return; // never send disabled rows
        const v = drafts[r.code];
        rates[r.code] = v === "" ? null : Number(v);
      });
      const { data } = await api.patch("/admin/settings/currency", { rates });
      setConfig(data.currency);
      if (data.changed?.length) {
        toast.success(`Rate updated: ${data.changed.join(", ")}`, {
          description: "All non-overridden INR prices refreshed instantly across the marketplace.",
        });
      } else {
        toast.info("No changes to save");
      }
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  if (loading || !config) {
    return <AdminLayout title="Currency"><div className="p-8 text-sm text-muted-foreground">Loading…</div></AdminLayout>;
  }

  const base = (config.base_currency || "USD").toUpperCase();
  const lastUpdated = config.rate_updated_at ? new Date(config.rate_updated_at).toLocaleString() : "Never";

  return (
    <AdminLayout title="Currency">
      <div className="max-w-4xl">
        <div className="mb-6">
          <div className="overline text-primary">Settings</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">Currency Management</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            The <strong>base currency</strong> is the single source of truth for every price on the platform. All other currencies are calculated live from these rates — no batch jobs, no stale data. When you update a rate here, the marketplace refreshes instantly.
          </p>
        </div>

        {/* Base currency */}
        <div className="border border-border bg-white p-5 mb-6" data-testid="currency-base-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-primary">Base currency</div>
              <div className="mt-1 flex items-center gap-3">
                <span className="font-display text-3xl">{base}</span>
                <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] bg-neutral-100 border border-border rounded-none">Immutable</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Every USD price entered on a plan or package is the master value. INR is derived.</div>
            </div>
            <TrendingUp size={32} className="text-primary/40" />
          </div>
        </div>

        {/* Rate table */}
        <div className="mb-6" data-testid="currency-rate-table">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline text-primary">Exchange rates</div>
              <div className="text-xs text-muted-foreground mt-1">Rate is <em>1 {base} → target</em>. Last updated: {lastUpdated}.</div>
            </div>
            <Button variant="outline" className="rounded-none" disabled title="Live FX feed is scheduled for a future release" data-testid="currency-fetch-live">
              <RefreshCw size={12} className="mr-2" /> Fetch live rate (soon)
            </Button>
          </div>

          <div className="border border-border bg-white divide-y divide-border">
            {CURRENCY_ROWS.map((row) => {
              const draftVal = drafts[row.code] ?? "";
              const currentVal = (config.rates || {})[row.code];
              const changed = row.enabled && (currentVal ?? null) !== (draftVal === "" ? null : Number(draftVal));
              return (
                <div key={row.code} className={`grid grid-cols-12 items-center gap-3 px-4 py-3 ${row.enabled ? "" : "bg-neutral-50 opacity-70"}`} data-testid={`currency-row-${row.code}`}>
                  <div className="col-span-4 flex items-center gap-3">
                    <span className="text-2xl">{row.flag}</span>
                    <div>
                      <div className="font-medium">{row.code}</div>
                      <div className="text-[11px] text-muted-foreground">{row.label}</div>
                    </div>
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground text-right">1 {base} =</div>
                  <div className="col-span-4">
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.01"
                        value={draftVal === null ? "" : draftVal}
                        onChange={(e) => setDrafts((p) => ({ ...p, [row.code]: e.target.value }))}
                        disabled={!row.enabled}
                        placeholder={row.enabled ? "e.g. 84.10" : "Coming soon"}
                        className="rounded-none pr-10"
                        data-testid={`currency-input-${row.code}`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">{row.symbol}</span>
                    </div>
                  </div>
                  <div className="col-span-2 text-right">
                    {!row.enabled ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground"><Lock size={10} /> Disabled</span>
                    ) : changed ? (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-amber-700">Unsaved</span>
                    ) : currentVal ? (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-700">Live</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Not set</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-start gap-2 text-[11px] text-muted-foreground border border-dashed border-border p-3 bg-neutral-50">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              <strong>How this works:</strong> Every plan package stores a single <strong>USD</strong> price. The INR shown on the site is <em>USD × rate</em>, computed on every read. Change the rate here → the whole marketplace updates instantly. Individual packages can still override INR via the <strong>Override INR</strong> toggle on the plan editor.
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end mb-10">
          <Button onClick={save} disabled={!dirty || saving} className="rounded-none bg-primary" data-testid="currency-save">
            {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save rates</>}
          </Button>
        </div>

        {/* History */}
        <div className="mb-10">
          <div className="overline text-primary mb-2">Rate history</div>
          {(config.history || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No rate changes recorded yet.</p>
          ) : (
            <div className="border border-border bg-white divide-y divide-border max-h-72 overflow-y-auto" data-testid="currency-history">
              {(config.history || []).map((h, i) => (
                <div key={i} className="px-4 py-2 flex items-center justify-between text-xs">
                  <div>
                    <span className="font-mono">{h.currency}</span>
                    <span className="text-muted-foreground mx-2">·</span>
                    <span>{h.old_rate ?? "—"} → <strong>{h.new_rate ?? "—"}</strong></span>
                  </div>
                  <div className="text-muted-foreground text-[11px]">
                    {h.changed_by_name} · {new Date(h.changed_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCurrency;
